import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Download, RefreshCcw } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { formatDate } from '../../lib/admin/utils';
import CorpStats from './CorpStats';
import { INDUSTRIES } from '../../pages/ff/CorpInquiryForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUSES = ['new', 'contacted', 'quoted', 'won', 'lost'];

const CorpTab = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [industry, setIndustry] = useState('all');

  const auth = () => ({ Authorization: `Bearer ${sessionStorage.getItem('adminToken')}` });

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const q = industry === 'all' ? '' : `?industry=${industry}`;
      const { data } = await axios.get(`${API}/corp/inquiries${q}`, { headers: auth() });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast({ title: 'Failed to load inquiries', description: err.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [industry]);

  useEffect(() => { fetch(); }, [fetch]);

  const changeStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/corp/inquiries/${id}/status`, { status }, { headers: auth() });
      toast({ title: 'Status updated', description: `Inquiry marked ${status}` });
      fetch();
    } catch (err) {
      toast({ title: 'Update failed', description: err.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const openLabel = async (id, index) => {
    try {
      const resp = await axios.get(`${API}/corp/inquiries/${id}/labels/${index}`, { headers: auth(), responseType: 'blob' });
      window.open(window.URL.createObjectURL(resp.data), '_blank');
    } catch { toast({ title: 'Could not open file', variant: 'destructive' }); }
  };

  const download = async () => {
    try {
      const resp = await axios.get(`${API}/corp/inquiries.csv`, { headers: auth(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'corp-inquiries.csv';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast({ title: 'Export failed', variant: 'destructive' }); }
  };

  return (
    <div className="space-y-4" data-testid="admin-corp-tab">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <div>
          <div className="text-sm text-gray-500">Total inquiries</div>
          <div className="text-2xl font-bold text-gray-900">{total}</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <select value={industry} onChange={(e) => setIndustry(e.target.value)}
                  className="border rounded-md px-3 py-1.5 text-sm" data-testid="corp-tab-industry">
            <option value="all">All industries</option>
            {INDUSTRIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <Button variant="outline" size="sm" onClick={fetch} disabled={loading} data-testid="corp-tab-refresh">
            <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button size="sm" onClick={download} data-testid="corp-tab-export">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      <CorpStats />

      <div className="bg-white rounded-lg border shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr className="text-left">
              <Th>Created</Th>
              <Th>Industry</Th>
              <Th>Name</Th>
              <Th>Business</Th>
              <Th>Email</Th>
              <Th>Phone</Th>
              <Th>Bottles</Th>
              <Th>Use case</Th>
              <Th>Label</Th>
              <Th>Path / Source</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={11} className="text-center py-8 text-gray-400">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={11} className="text-center py-8 text-gray-400">No inquiries yet.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-b last:border-b-0 hover:bg-gray-50">
                <Td>{r.createdAt ? formatDate(r.createdAt) : ''}</Td>
                <Td><span className="text-xs font-semibold uppercase text-[#722F37]">{(r.industry || '').replace('_', ' ')}</span></Td>
                <Td>{r.fullName}</Td>
                <Td>{r.businessName || '—'}</Td>
                <Td className="font-mono text-xs">{r.email}</Td>
                <Td className="font-mono text-xs">{r.phone}</Td>
                <Td>{(r.bottleRange || '').replace('_', '–')}</Td>
                <Td className="max-w-[240px] truncate" title={r.useCase || ''}>{r.useCase || '—'}</Td>
                <Td className="text-xs">
                  {r.customLabel || '—'}
                  {r.emails && (
                    <div className="mt-1" title={`Internal: ${r.emails.internal ? 'sent' : 'FAILED'} · Customer: ${r.emails.customer ? 'sent' : 'FAILED'}`} data-testid={`corp-emails-${r.id}`}>
                      <span className={r.emails.internal ? 'text-green-700' : 'text-red-600'}>✉ team {r.emails.internal ? '✓' : '✗'}</span>
                      <span className={`ml-2 ${r.emails.customer ? 'text-green-700' : 'text-red-600'}`}>✉ client {r.emails.customer ? '✓' : '✗'}</span>
                    </div>
                  )}
                  {(r.labelFiles || []).map((f) => (
                    <button key={f.index} onClick={() => openLabel(r.id, f.index)} className="block text-[#722F37] underline truncate max-w-[160px]" title={f.name} data-testid={`corp-label-file-${r.id}-${f.index}`}>{f.name}</button>
                  ))}
                </Td>
                <Td className="text-xs">
                  <span className={`px-1.5 py-0.5 rounded font-semibold ${r.intent === 'partner' ? 'bg-amber-100 text-amber-800' : r.intent === 'order' ? 'bg-rose-100 text-rose-800' : 'bg-gray-100 text-gray-500'}`}>
                    {r.intent || 'direct'}
                  </span>
                  <span className="ml-1 text-gray-500">{r.utmSource || ''}</span>
                </Td>
                <Td>
                  <select
                    value={r.status || 'new'}
                    onChange={(e) => changeStatus(r.id, e.target.value)}
                    className="border rounded px-2 py-1 text-xs"
                    data-testid={`corp-status-${r.id}`}
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Th = ({ children }) => (
  <th className="px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">{children}</th>
);
const Td = ({ children, className = '', title }) => (
  <td className={`px-4 py-2.5 ${className}`} title={title}>{children}</td>
);

export default CorpTab;
